/**
 * useUploadPickerMenu — пикер источника файла у слота загрузки:
 *   [⬆ Загрузить фото/видео]  [🖼 Из моих генераций]
 *
 * ЕДИНЫЙ механизм для обоих баров (перенос 1:1 из GenerateInputBar):
 *   1. GenerateInputBar — классический бар (/generate, /bots, home);
 *   2. GenerateToolMode — фокус-режим инструмента карточки.
 *
 * Возвращает:
 *   • makeShowPicker — фабрика для UploadsRow/UploadThumb: клик по слоту
 *     открывает fixed-портал UploadPickMenu у кнопки слота;
 *   • pickerPortal / galleryModal — готовые ноды, рендерятся в конце JSX.
 *
 * Внутри — весь цикл «Из моих генераций»: GalleryPickerModal по типу слота,
 * плейсхолдер-прелоадер в ПРАВИЛЬНОМ стейте (multi → referenceImages,
 * иначе uploadedImage/uploadedVideo), скачивание файла и прогон через
 * штатные handle*Upload (валидации/превью/pending — как у ручной загрузки).
 */
import { useCallback, useEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'

import { useGenerate } from '../../../context/GenerateContext'
import { useAuth } from '../../../context/AuthContext'
import { fetchImageAsFile } from '../utils/uploadHelpers'

import UploadPickMenu, { type PickMenuContext } from '../components/UploadPickMenu'
import { openScenePicker } from '../../../api/sceneSelection'
import { useSceneTools } from '../../../hooks/useSceneTools'
import GalleryPickerModal from '../components/GalleryPickerModal'
import { hasFrameRefModes } from '../../../utils/frameRefModes'

export type VideoTarget = 'uploadedVideo' | 'referenceVideo' | 'referenceVideos'
/** Куда класть картинку из галереи. У видео такой выбор был всегда, у картинок
 *  его не было: галерея ВСЕГДА писала в uploadedImage, откуда бы её ни открыли.
 *  На двух одинаковых квадратах это не замечали, а с именованными слотами
 *  «Ваше фото» уезжало в «картинку-основу». */
export type ImageTarget = 'uploadedImage' | 'swapImage' | 'endImage' | 'slot'

export function useUploadPickerMenu(opts?: {
	/** Карточка скрыла «Персонажа» (hide_character) — пункт прячем и в меню слота. */
	hideCharacter?: boolean
	/** Лимит фото карточки (user_photo_limit): выбор из галереи не пробивает
	 *  потолок — старшие вытесняются, при лимите 1 выбор ЗАМЕНЯЕТ фото. */
	photoLimit?: number | null
	/** Пайп тренда: в меню только «Персонаж», выбор встаёт в слот героя (режим пикера 'pipe'). */
	pipe?: boolean
}) {
	const hideCharacter = !!opts?.hideCharacter
	const pipeMode = !!opts?.pipe
	// Библиотека сцены закрыта этому пользователю — в меню слота остаётся один «Персонаж» (свои), как было до неё.
	const sceneTools = useSceneTools()
	const photoLimit = opts?.photoLimit ?? null
	const gen = useGenerate()
	const auth = useAuth()
	const {
		selectedModel: model,
		settings,
		requiresImage,
		requiresTwoImages,
		supportsMultipleImages,
		fileInputRef,
		handleImageUpload,
		handleSwapImageUpload,
		handleVideoUpload,
		handleReferenceVideoUpload,
		handleReferenceVideosUpload,
		handleReferenceAudiosUpload,
		setIsUploading,
		setReferenceImages,
		setReferenceVideo,
		setReferenceVideos,
		setReferenceAudios,
		setUploadedImage,
		setSwapImage,
		setEndImage,
		handleEndImageUpload,
		setUploadedVideo,
		handleSlotImageUpload,
		setSlotImagePending,
	} = gen

	const [uploadPickerOpen, setUploadPickerOpen] = useState(false)
	const [galleryPickerOpen, setGalleryPickerOpen] = useState(false)
	const [uploadPickerPos, setUploadPickerPos] = useState<{
		bottom: number
		left: number
		arrowLeft: number
	} | null>(null)
	const [pickerType, setPickerType] = useState<'images' | 'videos' | 'audios'>('images')
	// Целевой КАНАЛ видео-выбора: у моделей видео живёт в разных стейтах
	// (uploadedVideo — видео движения; referenceVideo/referenceVideos —
	// референсы). Без этого выбор из галереи падал в невидимый uploadedVideo
	// у референс-моделей (Seedance) — «видео не добавляется».
	const [videoTarget, setVideoTarget] = useState<VideoTarget>('uploadedVideo')
	const [imageTarget, setImageTarget] = useState<ImageTarget>('uploadedImage')
	// Именованный фото-слот карточки (25.09): галерея кладёт файл в ЭТОТ слот, а не в общий канал.
	const [slotIndex, setSlotIndex] = useState(0)
	// Шапка меню «для кого выбираем» (18.10): «Повторить движение» показывает кадр человека из ролика и подпись.
	const [pickerContext, setPickerContext] = useState<PickMenuContext | null>(null)
	const pickerInputRef = useRef<React.RefObject<HTMLInputElement | null>>(fileInputRef)

	const makeShowPicker = useCallback(
		(
			type: 'images' | 'videos' | 'audios',
			ref: React.RefObject<HTMLInputElement | null>,
			target?: VideoTarget,
			imgTarget?: ImageTarget,
			slotIdx?: number,
			context?: PickMenuContext | null,
		) =>
			(buttonEl: HTMLElement) => {
				const rect = buttonEl.getBoundingClientRect()
				pickerInputRef.current = ref
				setPickerType(type)
				setVideoTarget(target ?? 'uploadedVideo')
				setImageTarget(imgTarget ?? 'uploadedImage')
				setSlotIndex(slotIdx ?? 0)
				setPickerContext(context ?? null)
				const PICKER_W = 180
				const MARGIN = 8
				const idealLeft = rect.left + rect.width / 2
				const clampedLeft = Math.max(
					MARGIN + PICKER_W / 2,
					Math.min(window.innerWidth - MARGIN - PICKER_W / 2, idealLeft),
				)
				setUploadPickerPos({
					bottom: window.innerHeight - rect.top + 8,
					left: clampedLeft,
					arrowLeft: PICKER_W / 2 + (idealLeft - clampedLeft),
				})
				setUploadPickerOpen(true)
			},
		[],
	)

	useEffect(() => {
		if (!uploadPickerOpen) return
		const handler = (e: MouseEvent) => {
			const target = e.target as HTMLElement
			if (!target.closest('.upload-picker')) setUploadPickerOpen(false)
		}
		document.addEventListener('mousedown', handler)
		return () => document.removeEventListener('mousedown', handler)
	}, [uploadPickerOpen])

	const handlePickFromGallery = useCallback(
		async (url: string, previewUrl?: string) => {
			setGalleryPickerOpen(false)
			// Прелоадер на слоте с первого мгновения. Критично класть его в ТОТ ЖЕ
			// стейт, который рендерится для текущей модели и куда handle*Upload
			// положит скачанный файл: multi-image модели рендерят referenceImages
			// (MultiImgRow), остальные — uploadedImage/uploadedVideo (UploadThumb).
			// Прежний вариант всегда писал в uploadedImage — у multi-моделей этот
			// стейт вообще не рендерится, и слот «молчал» все 5-40с скачивания.
			// preview='' у старых файлов без webp-превью: тамб тёмный, dots видны.
			const isSeedance2Frames =
				hasFrameRefModes(model) &&
				(settings?.seedanceMode ?? 'frames') === 'frames'
			const isSlot = pickerType === 'images' && imageTarget === 'slot'
			const isMultiImage =
				pickerType === 'images' && !isSlot &&
				supportsMultipleImages && !requiresImage && !requiresTwoImages && !isSeedance2Frames
			const placeholder = { file: new File([], ''), preview: previewUrl || '' }
			if (pickerType === 'audios') setReferenceAudios((prev) => [...prev, placeholder])
			else if (pickerType === 'videos') {
				if (videoTarget === 'referenceVideos') setReferenceVideos((prev) => [...prev, placeholder])
				else if (videoTarget === 'referenceVideo') setReferenceVideo(placeholder)
				else setUploadedVideo(placeholder)
			} else if (isMultiImage) {
				// Лимит фото карточки: не даём галерее пробить потолок — держим
				// eff-1 последних + placeholder (реальный файл придёт отдельным
				// элементом после скачивания и займёт освободившееся место).
				const eff = photoLimit
					? Math.max(1, Math.min(model?.maxImages ?? 1, photoLimit))
					: null
				setReferenceImages((prev) => [
					...(eff ? prev.slice(Math.max(0, prev.length - (eff - 1))) : prev),
					placeholder,
				])
			} else if (isSlot) setSlotImagePending(slotIndex, previewUrl || '')
			else if (imageTarget === 'swapImage') setSwapImage(placeholder)
			else if (imageTarget === 'endImage') setEndImage(placeholder)
			else setUploadedImage(placeholder)
			setIsUploading(true)
			try {
				const file = await fetchImageAsFile(url, 'gallery')
				// handle*Upload синхронно перезапишет uploadedImage/uploadedVideo;
				// в multi-ряду реальный файл придёт ОТДЕЛЬНЫМ элементом, поэтому
				// placeholder снимаем сами (тот же React-батч — без кадра пустоты).
				if (isMultiImage) setReferenceImages((prev) => prev.filter((i) => i !== placeholder))
				if (pickerType === 'audios') setReferenceAudios((prev) => prev.filter((i) => i !== placeholder))
				if (pickerType === 'videos' && videoTarget === 'referenceVideos')
					setReferenceVideos((prev) => prev.filter((i) => i !== placeholder))
				if (file) {
					// Синтетическое событие — handleVideoUpload/handleImageUpload
					// читают только `target.files`, остальные поля ChangeEvent не
					// нужны. Cast через `unknown` обходит структурный typecheck
					// без расширения публичного API (вынос «синтетических» helper-
					// событий в отдельную util-функцию — отдельная задача).
					const syntheticEvent = {
						target: { files: [file] },
					} as unknown as React.ChangeEvent<HTMLInputElement>
					if (pickerType === 'audios') {
						await handleReferenceAudiosUpload(syntheticEvent)
					} else if (pickerType === 'videos') {
						if (videoTarget === 'referenceVideos') await handleReferenceVideosUpload(syntheticEvent)
						else if (videoTarget === 'referenceVideo') await handleReferenceVideoUpload(syntheticEvent)
						else await handleVideoUpload(syntheticEvent)
					} else if (isSlot) {
						// Слот карточки: тот же путь, что у ручной загрузки в слот (валидация, превью, S3, зеркало в referenceImages).
						await handleSlotImageUpload(slotIndex, syntheticEvent)
					} else if (imageTarget === 'swapImage') {
						await handleSwapImageUpload(syntheticEvent)
					} else if (imageTarget === 'endImage') {
						await handleEndImageUpload(syntheticEvent)
					} else {
						await handleImageUpload(syntheticEvent)
					}
				} else {
					// Скачивание не удалось — не оставляем висеть placeholder
					if (pickerType === 'videos') {
						if (videoTarget === 'referenceVideo') setReferenceVideo(null)
						else if (videoTarget === 'uploadedVideo') setUploadedVideo(null)
					} else if (pickerType === 'images' && !isMultiImage) {
						if (isSlot) setSlotImagePending(slotIndex, null)
						else if (imageTarget === 'swapImage') setSwapImage(null)
						else if (imageTarget === 'endImage') setEndImage(null)
						else setUploadedImage(null)
					}
				}
			} catch {
				if (pickerType === 'audios') setReferenceAudios((prev) => prev.filter((i) => i !== placeholder))
				else if (pickerType === 'videos') {
					if (videoTarget === 'referenceVideos') setReferenceVideos((prev) => prev.filter((i) => i !== placeholder))
					else if (videoTarget === 'referenceVideo') setReferenceVideo(null)
					else setUploadedVideo(null)
				} else if (isMultiImage) setReferenceImages((prev) => prev.filter((i) => i !== placeholder))
				else if (isSlot) setSlotImagePending(slotIndex, null)
				else if (imageTarget === 'endImage') setEndImage(null)
				else setUploadedImage(null)
			} finally {
				setIsUploading(false)
			}
		},
		[
			handleEndImageUpload,
			setEndImage,
			handleImageUpload,
			handleVideoUpload,
			handleReferenceVideoUpload,
			handleReferenceVideosUpload,
			handleReferenceAudiosUpload,
			setReferenceAudios,
			setReferenceVideo,
			setReferenceVideos,
			videoTarget,
			imageTarget,
			setSwapImage,
			handleSwapImageUpload,
			handleSlotImageUpload,
			setSlotImagePending,
			slotIndex,
			model,
			photoLimit,
			pickerType,
			requiresImage,
			requiresTwoImages,
			setIsUploading,
			setReferenceImages,
			setUploadedImage,
			setUploadedVideo,
			settings,
			supportsMultipleImages,
		],
	)

	// ── Готовые ноды: {pickerPortal}{galleryModal} в конце JSX бара ──
	const pickerPortal =
		uploadPickerOpen && uploadPickerPos
			? createPortal(
					<UploadPickMenu
						kind={pickerType}
						context={pickerContext}
						style={{
							position: 'fixed',
							bottom: uploadPickerPos.bottom,
							left: uploadPickerPos.left,
						}}
						onPickFile={() => {
							pickerInputRef.current?.current?.click()
							setUploadPickerOpen(false)
						}}
						onPickGallery={() => {
							setUploadPickerOpen(false)
							setGalleryPickerOpen(true)
						}}
						onPickScene={
							// Только фото-слоты и только авторизованным: гостю пункты
							// не показываем (не дразним недоступным). Карточка с
							// hide_character прячет сцену и здесь — её у карточки нет.
							pickerType === 'images' && !!auth.user && !hideCharacter
								? (kind) => {
										setUploadPickerOpen(false)
										openScenePicker({ kind, mode: pipeMode ? 'pipe' : 'scene' })
									}
								: undefined
						}
						sceneKinds={pipeMode || !sceneTools ? ['character'] : undefined}
					/>,
					document.body,
				)
			: null

	const galleryModal = galleryPickerOpen ? (
		<GalleryPickerModal
			initialTab={pickerType}
			onSelect={handlePickFromGallery}
			onClose={() => setGalleryPickerOpen(false)}
		/>
	) : null

	return { makeShowPicker, pickerPortal, galleryModal }
}
